import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    ButtonBase,
    Box,
    Menu,
    MenuItem,
    IconButton,
    Chip,
    Divider
} from '@mui/material';
import {
    AccountCircle,
    ArrowDropDown,
    Menu as MenuIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { NAV_ENTRIES, ADMIN_ENTRIES, ROLE_LABELS, isActive } from '../../routes/landing';

const BAR_HEIGHT = 56;

// One top-bar entry (Figma "Nav item"): muted when idle, bold with a 3px underline when active
const NavItem = React.forwardRef(({ active, children, ...props }, ref) => (
    <ButtonBase
        ref={ref}
        aria-current={active ? 'page' : undefined}
        sx={{
            position: 'relative',
            height: BAR_HEIGHT,
            px: 1.5,
            fontSize: '0.875rem',
            fontWeight: active ? 600 : 500,
            color: active ? 'text.primary' : 'text.secondary',
            transition: (theme) => theme.transitions.create('color', { duration: theme.transitions.duration.shortest }),
            '&::after': {
                content: '""',
                position: 'absolute',
                left: 12,
                right: 12,
                bottom: 0,
                height: 3,
                borderRadius: '3px 3px 0 0',
                backgroundColor: 'primary.main',
                transform: active ? 'scaleX(1)' : 'scaleX(0)',
                transition: (theme) => theme.transitions.create('transform', { duration: theme.transitions.duration.short }),
            },
            '&:hover': { color: 'text.primary' },
            '&:hover::after': { transform: active ? 'scaleX(1)' : 'scaleX(0.4)' },
            '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.light', outlineOffset: -4, borderRadius: 1 },
        }}
        {...props}
    >
        {children}
    </ButtonBase>
));

// App shell top bar (wf-shell-nav 1–5, YES-262). Entries follow the caller's permissions from
// /api/auth/me. Until the role is known (signed out, loading, session ended, /me failed) the bar
// shows the logo only, so it never guesses a role.
const Navbar = () => {
    const { user, status, can, logout } = useAuth();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [accountAnchor, setAccountAnchor] = React.useState(null);
    const [adminAnchor, setAdminAnchor] = React.useState(null);
    const [mobileAnchor, setMobileAnchor] = React.useState(null);

    const roleKnown = status === 'ready' && Boolean(user);
    const entries = roleKnown ? NAV_ENTRIES.filter((entry) => entry.show({ can, user })) : [];
    const adminEntries = roleKnown ? ADMIN_ENTRIES.filter((entry) => entry.show({ can, user })) : [];
    const adminActive = pathname.startsWith('/admin');

    const closeMenus = () => {
        setAccountAnchor(null);
        setAdminAnchor(null);
        setMobileAnchor(null);
    };

    const handleSignOut = async () => {
        closeMenus();
        await logout();
        navigate('/login');
    };

    return (
        <AppBar position="sticky" component="header">
            <Toolbar disableGutters sx={{ minHeight: { xs: BAR_HEIGHT }, px: { xs: 1, sm: 3 }, gap: { xs: 1, md: 3 } }}>
                {entries.length + adminEntries.length > 0 && (
                    <IconButton
                        aria-label="Open navigation"
                        aria-haspopup="true"
                        aria-expanded={Boolean(mobileAnchor)}
                        onClick={(e) => setMobileAnchor(e.currentTarget)}
                        sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}

                <Typography
                    component={RouterLink}
                    to="/"
                    aria-label="ADAS home"
                    sx={{
                        fontFamily: (theme) => theme.typography.h1.fontFamily,
                        fontWeight: 800,
                        fontSize: '1.125rem',
                        letterSpacing: '-0.02em',
                        color: 'primary.main',
                        textDecoration: 'none',
                        px: { xs: 0.5, sm: 0 },
                        borderRadius: 1,
                    }}
                >
                    ADAS
                </Typography>

                {roleKnown && (
                    <Box component="nav" aria-label="Main" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                        {entries.map((entry) => (
                            <NavItem key={entry.to} component={RouterLink} to={entry.to} active={isActive(pathname, entry.match)}>
                                {entry.label}
                            </NavItem>
                        ))}
                        {adminEntries.length > 0 && (
                            <NavItem
                                active={adminActive}
                                aria-haspopup="true"
                                aria-expanded={Boolean(adminAnchor)}
                                onClick={(e) => setAdminAnchor(e.currentTarget)}
                            >
                                Admin
                                <ArrowDropDown fontSize="small" sx={{ ml: 0.25, mr: -0.5 }} />
                            </NavItem>
                        )}
                    </Box>
                )}

                <Box sx={{ flexGrow: 1 }} />

                {roleKnown && (
                    <>
                        {user.role && (
                            <Chip
                                label={ROLE_LABELS[user.role] || user.role}
                                size="small"
                                variant="outlined"
                                sx={{
                                    display: { xs: 'none', sm: 'inline-flex' },
                                    borderRadius: 999,
                                    color: 'text.secondary',
                                    bgcolor: 'background.default',
                                    borderColor: 'divider',
                                    fontSize: '0.75rem',
                                }}
                            />
                        )}
                        <IconButton
                            aria-label="Account"
                            aria-haspopup="true"
                            aria-expanded={Boolean(accountAnchor)}
                            onClick={(e) => setAccountAnchor(e.currentTarget)}
                            sx={{
                                color: pathname === '/profile' ? 'primary.main' : 'text.secondary',
                                bgcolor: pathname === '/profile' ? 'action.selected' : 'transparent',
                                '&:hover': { color: 'text.primary' },
                            }}
                        >
                            <AccountCircle />
                        </IconButton>
                    </>
                )}
            </Toolbar>

            {/* Phone width: the entries move into this menu */}
            <Menu anchorEl={mobileAnchor} open={Boolean(mobileAnchor)} onClose={closeMenus} MenuListProps={{ 'aria-label': 'Main' }}>
                {entries.map((entry) => (
                    <MenuItem
                        key={entry.to}
                        component={RouterLink}
                        to={entry.to}
                        onClick={closeMenus}
                        selected={isActive(pathname, entry.match)}
                    >
                        {entry.label}
                    </MenuItem>
                ))}
                {adminEntries.length > 0 && entries.length > 0 && <Divider />}
                {adminEntries.map((entry) => (
                    <MenuItem key={entry.to} component={RouterLink} to={entry.to} onClick={closeMenus} selected={pathname.startsWith(entry.to)}>
                        Admin · {entry.label}
                    </MenuItem>
                ))}
            </Menu>

            <Menu anchorEl={adminAnchor} open={Boolean(adminAnchor)} onClose={closeMenus} PaperProps={{ sx: { minWidth: 200 } }}>
                {adminEntries.map((entry) => (
                    <MenuItem
                        key={entry.to}
                        component={RouterLink}
                        to={entry.to}
                        onClick={closeMenus}
                        selected={pathname.startsWith(entry.to)}
                        sx={{ fontWeight: pathname.startsWith(entry.to) ? 600 : 400 }}
                    >
                        {entry.label}
                    </MenuItem>
                ))}
            </Menu>

            <Menu
                anchorEl={accountAnchor}
                open={Boolean(accountAnchor)}
                onClose={closeMenus}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { minWidth: 180 } }}
            >
                <MenuItem component={RouterLink} to="/profile" onClick={closeMenus} selected={pathname === '/profile'}>
                    Profile
                </MenuItem>
                <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
            </Menu>
        </AppBar>
    );
};

export default Navbar;
